import axios from "axios";
import { Link, Outlet, useLocation, useNavigation } from "react-router-dom";
import {
  AppShell,
  NavLink,
  Divider,
  Group,
  ActionIcon,
  Menu,
  Text,
  Paper,
  rem,
} from "@mantine/core";
import {
  IconSpeakerphone,
  IconDeviceDesktop,
  IconCalendar,
  IconIcons,
  IconId,
  IconCirclePlus,
  IconWorld,
  IconUser,
  IconUsers,
  IconSocial
} from "@tabler/icons-react";
import { mtsUrl } from "../lib/mts-url";
import csrfToken from "../lib/csrf-token";
import Can from "../components/Can";
import { useRequests } from "../reducers/requestsReducer";

import classes from './Root.module.css';

function onLogoutClick() {
  axios.delete(mtsUrl('users/sign_out.json'), {
    withCredentials: true,
    headers: { 'X-CSRF-Token': csrfToken() },
  }).then(() => {
    window.location.href = mtsUrl('users/sign_in');
  });
}

type MenuOption = 'all_requests' |
  'business_card_requests' |
  'design_project_requests' |
  'event_requests' |
  'marketing_campaign_requests' |
  'social_media_post_requests' |
  'web_update_requests' |
  'new_request' |
  'manage_users';

export default function Root() {
  const location = useLocation();
  const { pathname, search } = location;
  const { filter } = useRequests();
  let typeFilter;
  if (filter.startsWith('type:')) typeFilter = filter.slice(5);
  let menuOption: MenuOption;

  if (pathname.indexOf('/new') >= 0) {
    menuOption = 'new_request';
  } else {
    const filterTypes = [
      'business_card_request',
      'design_project_request',
      'event_request',
      'marketing_campaign_request',
      'social_media_post_request',
      'web_update_request',
    ];
    
    if (typeFilter && filterTypes.includes(typeFilter))
      menuOption = `${typeFilter}s` as MenuOption;
    else
      menuOption = 'all_requests';
  }
  
  return (
    <AppShell
      bg="gray.2"
      header={{
        height: 70
      }}
      navbar={{
        width: 230,
        breakpoint: 'sm',
      }}
    >
      <AppShell.Header>
        <Group h="100%">
          <div style={{flexGrow: 1}}>
            <Text mx="md" fw={700} size={rem(20)} style={{letterSpacing: '-0.75px'}}>
              Marcomm Ticket System
            </Text>
          </div>
          <Menu>
            <Menu.Target>
              <ActionIcon variant="transparent" mx="md">
                <IconUser />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item>
                <Link to="/settings">Settings</Link>
              </Menu.Item>
              <Menu.Item onClick={onLogoutClick}>Logout</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar bg="gray.2" withBorder={false}>
        <Paper bg="gray.2" mt="lg" mr="lg">
          <NavLink
            className={classes.primaryNavLink}
            to="/requests"
            label="All Requests"
            fw={500}
            component={Link}
            leftSection={<IconWorld stroke={1.25} />}
            active={menuOption === 'all_requests'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:business_card_request"
            label="Business cards"
            fw={500}
            component={Link}
            leftSection={<IconId stroke={1.25} />}
            active={menuOption === 'business_card_requests'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:design_project_request"
            label="Design projects"
            fw={500}
            component={Link}
            leftSection={<IconIcons stroke={1.25} />}
            active={menuOption === 'design_project_requests'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:event_request"
            label="Events"
            fw={500}
            component={Link}
            leftSection={<IconCalendar stroke={1.25} />}
            active={menuOption === 'event_requests'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:marketing_campaign_request"
            label="Marketing campaigns"
            fw={500}
            component={Link}
            leftSection={<IconSpeakerphone stroke={1.25} />}
            active={menuOption === 'marketing_campaign_requests'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:social_media_post_request"
            label="Social media posts"
            fw={500}
            component={Link}
            leftSection={<IconSocial stroke={1.25} />}
            active={menuOption === 'social_media_post_requests'}
          />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests?filter=type:web_update_request"
            label="Web updates"
            fw={500}
            component={Link}
            leftSection={<IconDeviceDesktop stroke={1.25} />}
            active={menuOption === 'web_update_requests'}
          />
          <Divider />
          <NavLink
            className={classes.primaryNavLink}
            to="/requests/new/type"
            component={Link}
            label="New request"
            fw={500}
            leftSection={<IconCirclePlus stroke={1.25} />}
            active={menuOption === 'new_request'}
          />
          <Can do="manage" on="User">
            <Divider />
            <NavLink
              className={classes.primaryNavLink}
              href="#required-for-focus"
              label="Users"
              fw={500}
              leftSection={<IconUsers stroke={1.25} />}
            />
          </Can>
        </Paper>
      </AppShell.Navbar>
      <AppShell.Main
        maw="var(--mantine-breakpoint-xl)"
        pb="lg"
      >
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
